/**
 * The theme context: the mode in force, its palette, and the setter.
 * There is exactly one provider, at the top of the app, and every
 * stratum reads usePalette() -- a component that renders outside the
 * provider gets the light palette, which is also what a browser paints
 * before any JavaScript runs.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { LIGHT, type Palette } from './palette';
import { paletteFor, useThemeMode, type ThemeMode } from './mode';

export interface Theme {
  readonly mode: ThemeMode;
  readonly palette: Palette;
  /** Choose a mode, or null to follow the machine again. */
  readonly setMode: (mode: ThemeMode | null) => void;
}

const ThemeContext = createContext<Theme>({
  mode: 'light',
  palette: LIGHT,
  setMode: () => undefined,
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useThemeMode();
  const theme = useMemo<Theme>(() => ({ mode, palette: paletteFor(mode), setMode }), [mode, setMode]);
  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

/** A fixed theme for tests and previews: render a stratum in a mode. */
export function FixedTheme({ mode, children }: { mode: ThemeMode; children: ReactNode }) {
  const theme = useMemo<Theme>(() => ({ mode, palette: paletteFor(mode), setMode: () => undefined }), [mode]);
  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Theme {
  return useContext(ThemeContext);
}

export function usePalette(): Palette {
  return useContext(ThemeContext).palette;
}
